import type { FunctionComponent, ReactNode, SVGProps } from 'react'
import { useEffect, useState } from 'react'

import { useAuth } from '@/hooks/use-auth'

import en from './langs/en.json'
import { DEFAULT_LANGUAGE, getInitialLanguage, isSupportedLang, type RWLanguage } from './utils'

export type Translations = typeof en

export const getDefaultMessages = (): Translations => en

export type TLang = {
  code: RWLanguage
  name: string
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>
}

export const getMessages = async (lang: RWLanguage): Promise<Translations> => {
  return await import(`./langs/${lang}.json`).then((mod: { default: Translations }) => mod.default)
}

const getMessagesWithFallback = async (lang: RWLanguage) => {
  const defaultMessages = getDefaultMessages()
  if (lang === DEFAULT_LANGUAGE) {
    return defaultMessages
  }

  const selectedMessages = await getMessages(lang)

  return { ...defaultMessages, ...selectedMessages }
}

export const useLocalization = () => {
  const { user } = useAuth()

  const [lang, setLang] = useState<RWLanguage>(() => getInitialLanguage(user?.preferred_language))

  const [messages, setMessages] = useState(() => getDefaultMessages())

  useEffect(function listenForLanguageChangeEvent() {
    const handleLanguageChange = (event: CustomEvent<{ lang?: RWLanguage }>) => {
      const requested = event.detail.lang ?? DEFAULT_LANGUAGE
      const nextLang: RWLanguage = isSupportedLang(requested) ? requested : DEFAULT_LANGUAGE
      setLang(nextLang)
      localStorage.setItem('lang', nextLang)
    }

    const listener = handleLanguageChange as EventListener
    window.addEventListener('languageChange', listener)

    return () => {
      window.removeEventListener('languageChange', listener)
    }
  }, [])

  useEffect(
    function syncMessagesForLanguage() {
      getMessagesWithFallback(lang)
        .then((resolvedMessages) => {
          setMessages(resolvedMessages)
        })
        .catch((error: unknown) => {
          console.error('Failed to fetch translations for lang ', lang, error)
        })
    },
    [lang],
  )

  return { lang, messages }
}

export const defaultRichTextElements = {
  br: () => <br />,
  hr: () => <hr />,
  ul: (...chunks: ReactNode[]) => <ul>{chunks}</ul>,
  li: (...chunks: ReactNode[]) => <li>{chunks}</li>,
  a: (...chunks: ReactNode[]) => <a>{chunks}</a>,
  b: (...chunks: ReactNode[]) => <b>{chunks}</b>,
  i: (...chunks: ReactNode[]) => <i>{chunks}</i>,
  span: (...chunks: ReactNode[]) => <span>{chunks}</span>,
  p: (...chunks: ReactNode[]) => <p>{chunks}</p>,
  div: (...chunks: ReactNode[]) => <div>{chunks}</div>,
  h1: (...chunks: ReactNode[]) => <h1>{chunks}</h1>,
  h2: (...chunks: ReactNode[]) => <h2>{chunks}</h2>,
  h3: (...chunks: ReactNode[]) => <h3>{chunks}</h3>,
  h4: (...chunks: ReactNode[]) => <h4>{chunks}</h4>,
  h5: (...chunks: ReactNode[]) => <h5>{chunks}</h5>,
  h6: (...chunks: ReactNode[]) => <h6>{chunks}</h6>,
}
